import React, { useEffect, useState } from 'react';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { Checkbox } from '../../../components/ui/Checkbox';
import Icon from '../../../components/AppIcon';
import ImageUploadZone from '../../proof-submission/components/ImageUploadZone';

const EditProofModal = ({ proof, proofNumber, isOpen, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    title: proof?.title || '',
    description: proof?.description || '',
    rating: proof?.rating || 5,
    isPublic: proof?.isPublic || true
  });

  const [sourceType, setSourceType] = useState(proof?.youtubeUrl ? 'youtube' : 'image');
  const [youtubeUrl, setYoutubeUrl] = useState(proof?.youtubeUrl || '');

  const [selectedImage, setSelectedImage] = useState(
    proof?.image && !proof?.youtubeUrl ? { file: null, preview: proof?.image } : null
  );
  const [errors, setErrors] = useState({});
  const [startedAt, setStartedAt] = useState('');
  const [endedAt, setEndedAt] = useState('');

  const toLocalInput = (date) => {
    const d = date instanceof Date ? date : new Date(date);
    if (Number.isNaN(d.getTime())) return '';
    const local = new Date(d.getTime() - d.getTimezoneOffset() * 60000);
    return local.toISOString().slice(0, 16);
  };

  useEffect(() => {
    if (!isOpen) return;

    setFormData({
      title: proof?.title || '',
      description: proof?.description || '',
      rating: proof?.rating || 5,
      isPublic: proof?.isPublic || true,
    });
    setSourceType(proof?.youtubeUrl ? 'youtube' : 'image');
    setYoutubeUrl(proof?.youtubeUrl || '');
    setSelectedImage(proof?.image && !proof?.youtubeUrl ? { file: null, preview: proof?.image } : null);
    setErrors({});
    const now = new Date();
    setStartedAt(toLocalInput(proof?.startedAt || now));
    setEndedAt(toLocalInput(proof?.endedAt || ''));
  }, [isOpen, proof?.id]);

  const handleImageSelect = (image, error) => {
    setSelectedImage(image);
    if (error) {
      setErrors((prev) => ({ ...prev, image: error }));
    } else {
      setErrors((prev) => {
        const next = { ...prev };
        delete next.image;
        return next;
      });
    }
  };

  const validate = () => {
    const next = {};
    if (sourceType === 'youtube') {
      const raw = String(youtubeUrl || '').trim();
      if (!raw) next.youtubeUrl = '유튜브 URL을 입력해주세요';
    }
    if (!formData?.title?.trim()) next.title = '제목을 입력해주세요';
    if (!formData?.description?.trim()) next.description = '설명을 입력해주세요';
    if (!formData?.rating) next.rating = '달성도를 평가해주세요';
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = (e) => {
    e?.preventDefault();
    if (!validate()) return;
    const num = proof?.number || proofNumber;
    const startValue = startedAt || toLocalInput(new Date());
    const startDate = new Date(startValue);
    const endDate = endedAt
      ? new Date(endedAt)
      : new Date(startDate.getTime() + 30 * 60 * 1000);
    onSave({
      ...proof,
      ...formData,
      number: num,
      selectedImage,
      sourceType,
      youtubeUrl,
      startedAt: startDate.toISOString(),
      endedAt: endDate.toISOString(),
    });
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-dialog bg-background/95 backdrop-blur-sm flex items-center justify-center p-4">
      <div
        className="bg-card rounded-lg shadow-gaming-lg w-full max-w-lg max-h-[90vh] overflow-y-auto"
      >
        <div className="sticky top-0 bg-card border-b border-border px-6 py-4 flex items-center justify-between">
          <h2 className="text-xl font-heading font-semibold text-foreground">
            {proof?.id ? '실행인증 수정' : '실행인증 업로드'}
          </h2>
          <button
            onClick={onClose}
            className="p-2 rounded-md text-muted-foreground hover:bg-muted transition-gaming"
          >
            <Icon name="X" size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => {
                  setSourceType('image');
                  setYoutubeUrl('');
                  setErrors((prev) => {
                    const next = { ...prev };
                    delete next.youtubeUrl;
                    return next;
                  });
                }}
                className={`px-3 py-2 rounded-md border transition-gaming text-sm font-medium ${
                  sourceType === 'image'
                    ? 'bg-primary text-primary-foreground border-primary'
                    : 'bg-background text-foreground border-border hover:bg-muted'
                }`}
              >
                이미지
              </button>
              <button
                type="button"
                onClick={() => {
                  setSourceType('youtube');
                  setSelectedImage(null);
                  setErrors((prev) => {
                    const next = { ...prev };
                    delete next.image;
                    return next;
                  });
                }}
                className={`px-3 py-2 rounded-md border transition-gaming text-sm font-medium ${
                  sourceType === 'youtube'
                    ? 'bg-primary text-primary-foreground border-primary'
                    : 'bg-background text-foreground border-border hover:bg-muted'
                }`}
              >
                유튜브 URL
              </button>
            </div>

            {sourceType === 'image' ? (
              <ImageUploadZone
                onImageSelect={handleImageSelect}
                selectedImage={selectedImage}
                error={errors?.image}
              />
            ) : (
              <div className="space-y-3">
                <Input
                  label="유튜브 URL"
                  type="text"
                  placeholder="https://www.youtube.com/watch?v=..."
                  value={youtubeUrl}
                  onChange={(e) => setYoutubeUrl(e?.target?.value)}
                  error={errors?.youtubeUrl}
                />

                {String(youtubeUrl || '').trim() ? (
                  <div className="rounded-lg overflow-hidden border border-border bg-muted">
                    <img
                      src={`https://img.youtube.com/vi/${(() => {
                        try {
                          const u = new URL(String(youtubeUrl || '').trim());
                          const host = (u.hostname || '').replace(/^www\./, '');
                          if (host === 'youtu.be') return (u.pathname || '').replace('/', '').slice(0, 11);
                          const v = u.searchParams.get('v');
                          if (v) return v.slice(0, 11);
                          const m = (u.pathname || '').match(/\/shorts\/([^/?#]+)/) || (u.pathname || '').match(/\/embed\/([^/?#]+)/);
                          if (m?.[1]) return String(m[1]).slice(0, 11);
                          return '';
                        } catch {
                          return '';
                        }
                      })()}/hqdefault.jpg`}
                      alt="YouTube thumbnail"
                      className="w-full h-48 object-cover"
                      onError={(e) => {
                        e.currentTarget.style.display = 'none';
                      }}
                    />
                  </div>
                ) : null}
              </div>
            )}
          </div>

          <Input
            label="제목"
            type="text"
            placeholder="증명 제목을 입력하세요"
            value={formData?.title}
            onChange={(e) => setFormData({ ...formData, title: e?.target?.value })}
            error={errors?.title}
          />

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              설명
            </label>
            <textarea
              className="w-full px-4 py-3 bg-background border border-input rounded-md text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring transition-gaming resize-none"
              rows={4}
              placeholder="증명에 대한 설명을 입력하세요"
              value={formData?.description}
              onChange={(e) => setFormData({ ...formData, description: e?.target?.value })}
            />
            {errors?.description && (
              <p className="text-sm text-error mt-2 caption">{errors?.description}</p>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="시작 날짜/시간"
              type="datetime-local"
              value={startedAt}
              onChange={(e) => setStartedAt(e.target.value)}
            />
            <Input
              label="종료 날짜/시간"
              type="datetime-local"
              value={endedAt}
              onChange={(e) => setEndedAt(e.target.value)}
              description="미입력 시 30분 후로 자동 설정"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-3">
              별점 평가
            </label>
            <div className="flex items-center space-x-2">
              {[1, 2, 3, 4, 5]?.map((star) => (
                <button
                  key={star}
                  type="button"
                  onClick={() => setFormData({ ...formData, rating: star })}
                  className="transition-gaming active:translate-y-px"
                >
                  <Icon
                    name="Star"
                    size={32}
                    color="currentColor"
                    className={star <= formData?.rating ? 'fill-current text-primary' : 'text-muted-foreground'}
                  />
                </button>
              ))}
            </div>
          </div>

          <Checkbox
            label="공개 설정"
            description="다른 사용자가 이 증명을 볼 수 있습니다"
            checked={formData?.isPublic}
            onChange={(e) => setFormData({ ...formData, isPublic: e?.target?.checked })}
          />

          <div className="flex items-center space-x-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              fullWidth
            >
              취소
            </Button>
            <Button
              type="submit"
              variant="default"
              fullWidth
            >
              {proof?.id ? '저장' : '업로드'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditProofModal;